import { EntityIdAdapter } from '@record-it-npm/synchro-common';
import { DeletionCascading } from '../DeletionCascading/DeletionCascading';
import {
  EntityActualization,
  GetActualizationsFunction
} from '../EntityActualization/EntityActualization';
import { EntityLocalIdUpdater } from '../EntityLocalIdUpdater/EntityLocalIdUpdater';
import {
  RepositoryByEntityName,
  EntityRepositoryContainer
} from '../EntityRepositoryContainer/EntityRepositoryContainer';
import { EntityStorage } from '../EntityStorage/EntityStorage';
import {
  EntitySynchronization,
  UploadFunction
} from '../EntitySynchronization/EntitySynchronization';
import { IdGenerator } from '../IdGenerator/IdGenerator';
import { CreateRepositoryByEntityName } from './CreateRepositoryByEntityName';
import { EntityManagerEntityTypesByEntityName } from '../EntityManagerEntityTypesByEntityName';
import { AnySynchronizationEnvironmentTypesConstraint } from '../SynchronizationEnvironmentTypes';
import {
  CreateEntityFilter,
  EntityFilterContainer
} from '../EntityFilter/EntityFilterContainer';

export class EntityManager<
  TSynchronizationEnvironmentTypes extends
    AnySynchronizationEnvironmentTypesConstraint,
  TEntityTypesByEntityName extends
    EntityManagerEntityTypesByEntityName<TSynchronizationEnvironmentTypes>,
  TRepositoryByEntityName extends RepositoryByEntityName<
    TSynchronizationEnvironmentTypes,
    TEntityTypesByEntityName
  >
> {
  private entityStorage: EntityStorage<
    TSynchronizationEnvironmentTypes,
    TEntityTypesByEntityName
  >;
  private idGenerator: IdGenerator;
  private filterContainer: EntityFilterContainer<
    TSynchronizationEnvironmentTypes,
    TEntityTypesByEntityName
  >;
  private internalEntityRepositoryContainer: EntityRepositoryContainer<
    TSynchronizationEnvironmentTypes,
    TEntityTypesByEntityName,
    TRepositoryByEntityName
  >;
  private internalEntityActualization: EntityActualization<
    TSynchronizationEnvironmentTypes,
    TEntityTypesByEntityName,
    TRepositoryByEntityName
  >;
  private internalEntitySynchronization: EntitySynchronization<
    TSynchronizationEnvironmentTypes,
    TEntityTypesByEntityName,
    TRepositoryByEntityName
  >;
  private internalDeletionCascading: DeletionCascading<
    TSynchronizationEnvironmentTypes,
    TEntityTypesByEntityName,
    TRepositoryByEntityName
  >;
  private entityLocalIdUpdater: EntityLocalIdUpdater<
    TSynchronizationEnvironmentTypes,
    TEntityTypesByEntityName,
    TRepositoryByEntityName
  >;

  constructor(
    options: EntityManagerOptions<
      TSynchronizationEnvironmentTypes,
      TEntityTypesByEntityName,
      TRepositoryByEntityName
    >
  ) {
    this.entityStorage = new EntityStorage<
      TSynchronizationEnvironmentTypes,
      TEntityTypesByEntityName
    >();

    this.idGenerator = new IdGenerator({
      entityIdAdapter: options.entityIdAdapter
    });

    this.filterContainer = new EntityFilterContainer({
      createEntityFilter: options.createEntityFilter
    });

    const repositoryByEntityName = options.createRepositoriesByEntityName({
      entityStorage: this.entityStorage,
      idGenerator: this.idGenerator,
      filterContainer: this.filterContainer
    });

    this.internalEntityRepositoryContainer = new EntityRepositoryContainer(
      repositoryByEntityName
    );
    this.internalEntityActualization = new EntityActualization(
      this.internalEntityRepositoryContainer,
      options.getActualizationsFunction
    );
    this.internalEntitySynchronization = new EntitySynchronization(
      this.internalEntityRepositoryContainer,
      options.uploadFunction
    );
    this.internalDeletionCascading = new DeletionCascading(
      this.internalEntityRepositoryContainer
    );
    this.entityLocalIdUpdater = new EntityLocalIdUpdater(
      this.internalEntitySynchronization,
      this.internalEntityRepositoryContainer
    );
  }

  public get entityRepositoryContainer(): EntityRepositoryContainer<
    TSynchronizationEnvironmentTypes,
    TEntityTypesByEntityName,
    TRepositoryByEntityName
  > {
    return this.internalEntityRepositoryContainer;
  }

  public get entityActualization(): EntityActualization<
    TSynchronizationEnvironmentTypes,
    TEntityTypesByEntityName,
    TRepositoryByEntityName
  > {
    return this.internalEntityActualization;
  }

  public get entitySynchronization(): EntitySynchronization<
    TSynchronizationEnvironmentTypes,
    TEntityTypesByEntityName,
    TRepositoryByEntityName
  > {
    return this.internalEntitySynchronization;
  }

  public get deletionCascading(): DeletionCascading<
    TSynchronizationEnvironmentTypes,
    TEntityTypesByEntityName,
    TRepositoryByEntityName
  > {
    return this.internalDeletionCascading;
  }

  public async init(): Promise<void> {
    await this.idGenerator.init();
    await this.entityStorage.init();

    await Promise.all(
      this.entityRepositoryContainer
        .getAll()
        .map((repository) => repository.init())
    );

    await this.internalEntityActualization.init();
    await this.internalEntitySynchronization.init();
    await this.internalDeletionCascading.init();
    await this.entityLocalIdUpdater.init();
    this.filterContainer.init();
  }

  public async flush(): Promise<void> {
    await this.entityStorage.flush();
    await this.internalEntityActualization.flush();
    await this.internalEntitySynchronization.flush();
  }

  public destroy(): void {
    this.filterContainer.destroy();

    for (const repository of this.entityRepositoryContainer.getAll()) {
      repository.destroy();
    }

    this.internalEntityActualization.destroy();
    this.internalEntitySynchronization.destroy();
    this.internalDeletionCascading.destroy();
    this.entityLocalIdUpdater.destroy();
    this.idGenerator.destroy();
    this.entityStorage.destroy();
  }
}

export type EntityManagerOptions<
  TSynchronizationEnvironmentTypes extends
    AnySynchronizationEnvironmentTypesConstraint,
  TEntityTypesByEntityName extends
    EntityManagerEntityTypesByEntityName<TSynchronizationEnvironmentTypes>,
  TRepositoryByEntityName extends RepositoryByEntityName<
    TSynchronizationEnvironmentTypes,
    TEntityTypesByEntityName
  >
> = {
  createRepositoriesByEntityName: CreateRepositoryByEntityName<
    TSynchronizationEnvironmentTypes,
    TEntityTypesByEntityName,
    TRepositoryByEntityName
  >;
  getActualizationsFunction: GetActualizationsFunction<
    TSynchronizationEnvironmentTypes,
    TEntityTypesByEntityName
  >;
  uploadFunction: UploadFunction<
    TSynchronizationEnvironmentTypes,
    TEntityTypesByEntityName
  >;
  entityIdAdapter: EntityIdAdapter;
  createEntityFilter: CreateEntityFilter<
    TSynchronizationEnvironmentTypes,
    TEntityTypesByEntityName
  >;
};
